import * as React from 'react'
import { useSearchParams } from '../../../../../utils/hooks'
import useDefaultValues from './useDefaultValues'

interface ChannelsFiltersParams {
    chains: number[];
}

type SetSearchParamsFn = (newSearchParams: Partial<ChannelsFiltersParams>) => void
type ResetSearchParamsFn = () => void

type UseChannelsSearchParamsOutput = [
    ChannelsFiltersParams,
    SetSearchParamsFn,
    ResetSearchParamsFn,
]

const ChannelFiltersSearchKeys = {
    chains: 'ch',
    search: 's',
}

/**
 * Hook for parsing and formatting channel filter values from and to the search params
 * @returns 
 */
export default function useChannelsFiltersParams(): UseChannelsSearchParamsOutput {
    const [searchParams, setSearchParams] = useSearchParams()
    const defaultFormValues = useDefaultValues()

    // Parsed search params
    const parsedSearchParams = React.useMemo(() => {
        const chains: number[] = searchParams.has(ChannelFiltersSearchKeys.chains)
            ? searchParams.get(ChannelFiltersSearchKeys.chains)
                ?.split(',')
                .filter(v => !!v)
                .map(v => parseInt(v, 10))
                    ?? []
            : defaultFormValues.chains
    
            return { chains }
    }, [defaultFormValues.chains, searchParams])

    // setSearchParams wrapper for formatting values before updating the URL search
    const customSetSearchParams = React.useCallback((newSearchParams: Partial<ChannelsFiltersParams>) => {
        setSearchParams(searchParams => {
            return {
                ...Object.fromEntries(searchParams),
                [ChannelFiltersSearchKeys.chains]: newSearchParams.chains?.sort().join(',') ?? '',
            }
        })
    }, [setSearchParams])

    // Function for resetting search params. It removes from search params all keys that belong to channel form
    const resetSearchParams = React.useCallback(() => {
        // Get all keys that belong to channel form
        const channelFormKeys = Object.values(ChannelFiltersSearchKeys)

        setSearchParams(searchParams => {
            // Transform current search params to object
            const objSearchParams = Object.fromEntries(searchParams)

            // Get all keys in current search params
            const currentSearchParamsKeys = Object.keys(objSearchParams)

            // Exclude keys that belong to channel form
            const keysNotFromChannelForm = currentSearchParamsKeys.filter((k) => !channelFormKeys.includes(k))

            // Build search params excluding channel form keys
            const searchWithoutChannelForm = keysNotFromChannelForm.reduce((acc, key) => {
                return {
                    ...acc,
                    [key]: objSearchParams[key as keyof typeof objSearchParams],
                }
            }, {})

            return searchWithoutChannelForm
        })
    }, [setSearchParams])

    return [parsedSearchParams, customSetSearchParams, resetSearchParams]    
}